import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import GenericSelect from '../../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import Button from '../../../components/ui/Button';
import { useActivePeriods, bulkAssignGoals, departmentAsync, employeeAsync } from '../config';

// Faz J (spec 2026-08-08-pm-hijyen-faz-j-design.md §4, J5, Task 4) —
// GoalTemplatesTab satır aksiyonu "Toplu Ata": aktif dönem + hedef kitle
// (departman TEK-seçim VEYA çalışan ÇOKLU-seçim — task-3-report.md §3
// "employeeIds ÖNCELİKLİ — doluysa departmentId GÖZ ARDI edilir"; FE bu
// önceliği bir MOD anahtarıyla açıkça tek yönlü tutar, ikisini birden
// GÖNDERMEZ) + gönder + sonuç özeti. NewMeetingModal/NewCycleModal'ın AYNI
// Overlay/Modal/Header/Footer iskeleti — iki AŞAMALI (form → sonuç) tek
// modal, sonuç aşamasında OTOMATİK KAPANMAZ (kullanıcı skipped listesini
// görmeli).
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 6px;
  width: 520px;
  max-width: 92vw;
  max-height: 88vh;
  overflow-y: auto;
  padding: 28px;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: #A0A8B4;
  &:hover:not(:disabled) { background: #F1F5F9; color: #004242; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const ModeRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
`;

const ModeOption = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

const HintText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748B;
  margin-bottom: 12px;
`;

const ErrorText = styled.p`
  font-size: 12px;
  color: #B02A37;
  margin: 8px 0;
  font-weight: 600;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
`;

const ResultCreated = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: #1B7A3E;
  margin-bottom: 14px;
`;

const SkippedTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const SkippedRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
  color: #B02A37;
  padding: 6px 0;
  border-bottom: 1px solid #F1F5F9;
  &:last-child { border-bottom: none; }
`;

// Müşteri önerisi (2026-08-10): modal iki kaynakla açılabilir — `template`
// (GoalTemplatesTab "Toplu Ata", Faz J davranışı birebir) VEYA `orgGoal`
// (Kurumsal Hedefler › "Çalışanlara Ata"). orgGoal modunda dönem kurumsal
// hedefin dönemine SABİTTİR (hizalama aynı dönemi şart koşar — BE alignInvalid),
// dönem seçici hiç gösterilmez; içerik BE'de kurumsal hedeften türetilir.
export default function BulkAssignModal({ template, orgGoal, onClose }) {
  const { t } = useTranslation();
  const isOrgGoalMode = !!orgGoal;
  const { periods, fetching: periodsFetching } = useActivePeriods();

  const [periodId, setPeriodId] = useState(isOrgGoalMode ? orgGoal.periodId : null);
  const [targetMode, setTargetMode] = useState('department');
  const [departmentId, setDepartmentId] = useState(null);
  const [employeeIds, setEmployeeIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorHint, setErrorHint] = useState('');
  const [result, setResult] = useState(null);

  const periodOptions = useMemo(() => periods.map((p) => ({ id: p.id, name: p.name })), [periods]);

  const targetProvided = targetMode === 'department' ? !!departmentId : employeeIds.length > 0;
  const valid = !!periodId && targetProvided;

  const handleModeChange = useCallback((mode) => {
    setTargetMode(mode);
    setDepartmentId(null);
    setEmployeeIds([]);
    setErrorHint('');
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!periodId) {
      setErrorHint(t('bulkAssign.periodRequiredHint'));
      return;
    }
    if (!targetProvided) {
      setErrorHint(t('bulkAssign.targetRequiredHint'));
      return;
    }
    setErrorHint('');
    setSubmitting(true);
    const payload = {
      periodId,
      goalTemplateId: template?.id,
      alignedOrgGoalId: orgGoal?.id,
      employeeIds: targetMode === 'employees' ? employeeIds : undefined,
      departmentId: targetMode === 'department' ? departmentId : undefined,
    };
    const res = await bulkAssignGoals(payload);
    setSubmitting(false);
    if (res) setResult(res);
  }, [periodId, targetProvided, targetMode, employeeIds, departmentId, template, orgGoal, t]);

  return (
    <Overlay onClick={submitting ? undefined : onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            {isOrgGoalMode
              ? t('bulkAssign.titleOrgGoal', { name: orgGoal?.title })
              : t('bulkAssign.title', { name: template?.name })}
          </Title>
          <CloseBtn onClick={onClose} disabled={submitting}><X size={18} /></CloseBtn>
        </Header>

        {result ? (
          <>
            <ResultCreated data-role="bulk-assign-created">
              {t('bulkAssign.resultCreated', { count: result.created })}
            </ResultCreated>
            {result.skipped?.length > 0 && (
              <>
                <SkippedTitle>{t('bulkAssign.resultSkippedTitle')}</SkippedTitle>
                {result.skipped.map((item) => (
                  <SkippedRow key={item.employeeId} data-role="bulk-assign-skipped-row">
                    <span>{item.employeeId}</span>
                    <span>{t(`bulkAssign.skipReason.${item.reason}`, { defaultValue: item.reason })}</span>
                  </SkippedRow>
                ))}
              </>
            )}
            <Footer>
              <Button onClick={onClose}>{t('bulkAssign.close')}</Button>
            </Footer>
          </>
        ) : (
          <>
            {!isOrgGoalMode && (
              <Field>
                <GenericSelect
                  label={t('bulkAssign.periodLabel')}
                  options={periodOptions}
                  value={periodId}
                  onChange={setPeriodId}
                  idKey="id"
                  labelKey="name"
                  required
                />
                {!periodsFetching && periods.length === 0 && (
                  <HintText>{t('bulkAssign.noPeriodsHint')}</HintText>
                )}
              </Field>
            )}
            {isOrgGoalMode && (
              <HintText>{t('bulkAssign.orgGoalHint')}</HintText>
            )}

            <ModeRow>
              <ModeOption>
                <input
                  type="radio"
                  name="bulk-assign-target-mode"
                  checked={targetMode === 'department'}
                  onChange={() => handleModeChange('department')}
                />
                {t('bulkAssign.targetModeDepartment')}
              </ModeOption>
              <ModeOption>
                <input
                  type="radio"
                  name="bulk-assign-target-mode"
                  checked={targetMode === 'employees'}
                  onChange={() => handleModeChange('employees')}
                />
                {t('bulkAssign.targetModeEmployees')}
              </ModeOption>
            </ModeRow>

            {targetMode === 'department' ? (
              <Field>
                <AsyncGenericSelect
                  label={t('bulkAssign.departmentLabel')}
                  value={departmentId}
                  onChange={setDepartmentId}
                  onSearch={departmentAsync.onSearch}
                  onGetById={departmentAsync.onGetById}
                />
              </Field>
            ) : (
              <Field>
                <AsyncGenericSelect
                  label={t('bulkAssign.employeesLabel')}
                  value={employeeIds}
                  onChange={(v) => setEmployeeIds(v || [])}
                  onSearch={employeeAsync.onSearch}
                  onGetById={employeeAsync.onGetById}
                  isMulti
                />
              </Field>
            )}

            {errorHint && <ErrorText>{errorHint}</ErrorText>}

            <Footer>
              <Button variant="outline" onClick={onClose} disabled={submitting}>
                {t('common.cancel')}
              </Button>
              <Button data-role="bulk-assign-submit-btn" onClick={handleSubmit} disabled={!valid || submitting}>
                {submitting ? t('common.saving') : t('bulkAssign.submit')}
              </Button>
            </Footer>
          </>
        )}
      </Modal>
    </Overlay>
  );
}
